/**
 * Utility functions for dealing with dates
 */

import { getNow } from '@sparx/sw-common';
import dateFormat from 'dateformat';
import moment from 'moment';

/**
 * Returns the date for the Monday of the week of the provided date (if it is
 * Sunday, we still use the Monday before)
 * @param date
 */
export const getMondayBefore = (date: Date) => {
    // getDay() returns the day of the week where Sunday = 0, ..., Saturday = 6.
    // To get Monday of the current week, we deduct {day of week} - 1 from
    // the date, but where Sunday = 7.
    const newDate = new Date(date.getTime());
    const dayOfWeek = newDate.getDay();
    const daysToDeduct = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    newDate.setDate(newDate.getDate() - daysToDeduct);
    return newDate;
};

/**
 * Generates a 4-digit pin code which is made up from the day and month of
 * the Monday of the week of the provided date (or the current date, if not
 * provided). If it is Sunday, we still use the Monday before.
 * @param date The date to check. If not provided, will use the current date
 */
export const getMondayBeforeCode = (date = getNow()) => dateFormat(getMondayBefore(date), 'ddmm');

/**
 * Takes a moment, returns a nicely formatted string for displaying how long ago it was
 * Can return:
 * 'today',
 * 'yesterday',
 * 'two days ago' -> 'six days ago',
 * 'a week ago' -> 'four weeks ago',
 * 'over a month ago'
 * @param d - date
 * @returns {string}
 */
export const getTimeAgoText = (d: moment.Moment) => {
    // Get the rounding default
    const roundingDefault = moment.relativeTimeRounding();

    // Set the rounding to floor, required for this agoFunc
    moment.relativeTimeRounding(Math.floor);

    const end = moment().endOf('day');
    const agoFunc = () =>
        '[' +
        (end.diff(d, 'weeks') > 4
            ? 'over a month ago'
            : end.diff(d, 'days') >= 14
            ? end.diff(d, 'weeks') + ' weeks ago' // 2-4 weeks ago
            : end.diff(d, 'days') >= 7
            ? 'a week ago'
            : d.from(end)) +
        ']';
    const str = d.calendar(undefined, {
        sameDay: '[today]',
        lastDay: '[yesterday]',
        lastWeek: agoFunc,
        sameElse: agoFunc,
        nextDay: '',
        nextWeek: '',
    });

    // Reset the relative time rounding to the default
    moment.relativeTimeRounding(roundingDefault);

    // Replace numbers with words
    // We know we can only have
    // min of 2 and
    // max of 6 (for six days ago) and 4 (for four weeks ago)
    return str
        .replace('2', 'two')
        .replace('3', 'three')
        .replace('4', 'four')
        .replace('5', 'five')
        .replace('6', 'six');
};

export const getHomeworkDueTimeString = (d: moment.Moment) =>
    d.get('minutes') === 0 ? d.format('ha') : d.format('h.mma');
